import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { useOrderPage } from '../orderPageContext/useOrderPage';
import { useIntentConfig } from '../intentConfig/useIntentConfig';
import { useSaveOrder } from './useSaveOrder';
import { OrderPageIntent } from '../OrderPageIntent';
import { OrderPageErrors } from './OrderPageErrors';
import { StepId } from '../intentConfig/types';
import OrderUtils from '../OrderUtils';
import { quotePrice } from '../core/pricing';
import { sellsBothVoucherTypes } from '../utils/stepHelpers';
import { OrderPageData, OrderService } from '../../api';
import {
  clearOrderPageStateFromLocalStorage,
  clearSessionExpiry,
  getSessionTtlLeft,
  setSessionExpiry,
} from '../orderPageContext/orderPageStateLocalStorage';

type UseOrderNavigationParams = {
  formRef: MutableRefObject<any>;
  personalDetailsRef: MutableRefObject<HTMLDivElement | null>;
  embeddedWidget: boolean;
  setError: (e: OrderPageErrors | undefined) => void;
  setErrorMessage: (m: string | undefined) => void;
  setLoading: (v: boolean) => void;
};

export function useOrderNavigation({
  formRef,
  personalDetailsRef,
  embeddedWidget,
  setError,
  setErrorMessage,
  setLoading,
}: UseOrderNavigationParams) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const {
    orderPageState,
    setActiveStep,
    setActiveStepAndOrderPageIntent,
    setOrderPageState,
    reloadDates,
    disclaimersAccepted,
    setDisclaimersError,
    setSessionTtlMilliseconds,
  } = useOrderPage();
  const { config, visibleSteps, currentStepId, currentStepIndex } = useIntentConfig();
  const nextStepId = visibleSteps[currentStepIndex + 1]?.id;
  const nextIsPayment = nextStepId === StepId.PAYMENT;
  const hasStepBeforePayment = visibleSteps.some(
    (s) =>
      s.id === StepId.DISCLAIMERS ||
      s.id === StepId.PASS_SCHEDULE_SELECTIONS ||
      s.id === StepId.PASS_DATED_SESSION_SELECTIONS,
  );
  const { orderPageIntent } = orderPageState;
  const [detailsStepErrors] = useState({});

  const { saveOrder, getPaymentForm } = useSaveOrder({
    embeddedWidget,
    onSaveError: (message?: string) => {
      setErrorMessage(message);
      setError(OrderPageErrors.SAVE_ORDER_EXCEPTION);
      setSessionTtlMilliseconds(null);
      setLoading(false);
    },
  });

  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const sessionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const initSessionExpiredTimeout = (ttl?: number) => {
    if (!ttl) {
      ttl = OrderUtils.ORDER_PAGE_TTL * 1000;
    }
    setSessionExpiry(orderPageState.orderPage.id, ttl);
    setSessionTtlMilliseconds(ttl);
    sessionTimeoutRef.current = setTimeout(() => {
      // eslint-disable-next-line no-alert
      alert('Session expired. please reload');
      clearOrderPageStateFromLocalStorage();
      clearSessionExpiry(orderPageState.orderPage.id);
      window.location.reload();
    }, ttl);
  };

  const clearSessionExpiredTimeout = () => {
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
    }
  };

  useEffect(() => {
    void (() => {
      if (currentStepId === StepId.PAYMENT) {
        const ttl = getSessionTtlLeft(orderPageState.orderPage.id);
        if (ttl > 0) {
          initSessionExpiredTimeout(ttl);
        }
      }
    })();
  }, []);

  const moveToNextStep = (intent?: OrderPageIntent) => {
    setLoading(false);
    const nextIndex = currentStepIndex + 1;
    if (intent) {
      setActiveStepAndOrderPageIntent(nextIndex, intent);
    } else {
      setActiveStep(nextIndex);
    }
  };

  const handleDetailsDateTimeSelectionNext = (intent?: OrderPageIntent) => {
    if (Object.keys(detailsStepErrors).length > 0) {
      return;
    }
    const isMonetaryGiftCard = intent === OrderPageIntent.MONETARY_GIFT_CARD;
    const goesToGiftCardTypeStep =
      intent === OrderPageIntent.GIFT_CARD && sellsBothVoucherTypes(orderPageState.orderPage as OrderPageData);
    if (
      orderPageIntent !== OrderPageIntent.BUY_PASS &&
      !isMonetaryGiftCard &&
      !goesToGiftCardTypeStep &&
      orderPageState.details.persons < 1
    ) {
      setError(OrderPageErrors.AT_LEAST_ONE_PERSON);
      return;
    }
    const needsDateValidation =
      intent !== OrderPageIntent.GIFT_CARD &&
      !isMonetaryGiftCard &&
      config?.requiresDateSelection &&
      currentStepId !== StepId.VOUCHER_SUMMARY;

    if (needsDateValidation && (!orderPageState.dateAndTimeDetails.date || !orderPageState.dateAndTimeDetails.time)) {
      setError(OrderPageErrors.DATE_AND_TIME_NOT_SELECTED);
      return;
    }
    if (isMonetaryGiftCard) {
      setOrderPageState({
        ...orderPageState,
        activeStep: currentStepIndex + 1,
        orderPageIntent: intent,
        details: { ...orderPageState.details, price: 0 },
      });
      setLoading(false);
      return;
    }
    moveToNextStep(intent);
  };

  const intentToTransactionType = (intent: OrderPageIntent): string => {
    switch (intent) {
      case OrderPageIntent.GIFT_CARD:
      case OrderPageIntent.MONETARY_GIFT_CARD:
        return 'voucher';
      case OrderPageIntent.BUY_PASS:
        return 'pass';
      default:
        return 'order';
    }
  };

  const handlePersonalDetailsNext = async () => {
    const data = formRef;
    if (!data.current) {
      return;
    }
    // @ts-ignore
    await data.current.submitForm();
    const { isValid } = data.current;
    if (isValid) {
      setLoading(true);
      // @ts-ignore
      const { values } = data.current;
      const personalDetails = {
        firstname: values.firstname,
        lastname: values.lastname,
        alternative_full_name: values.receipt_in_other_name ? values.alternative_full_name.trim() : undefined,
        email: values.email,
        phone: values.phone.replace('-', ''),
        custom_personal_details: values.custom_personal_details,
        referral_source: values.referral_source,
        voucher_greeting_message: values.voucher_greeting_message,
        recipient_email: values.recipient_email,
        delivery_date: values.delivery_date,
        delivery_time: values.delivery_time,
        should_send_delayed:
          !!values.recipient_email &&
          values.should_send_delayed &&
          Boolean(values.delivery_date && values.delivery_time),
        pass_start_date: values.pass_start_date,
        save_card_consent: values.save_card_consent,
      };
      if (orderPageIntent === OrderPageIntent.REDEEM_GIFT_CARD) {
        const redemptionOrder = await saveOrder(personalDetails);
        if (!redemptionOrder) {
          return;
        }

        setOrderPageState({
          ...orderPageState,
          activeStep: currentStepIndex + 1,
          personalDetails,
          order_id: redemptionOrder.id,
          returningCustomer: redemptionOrder.returning_customer || false,
        });
        setLoading(false);
        return;
      }

      if (!nextIsPayment) {
        setOrderPageState({
          ...orderPageState,
          activeStep: currentStepIndex + 1,
          personalDetails,
        });
        setLoading(false);
        return;
      }

      let url = '';
      const order = await saveOrder(personalDetails);
      if (!order) {
        // saveOrder already surfaced the error and stopped the loader
        return;
      }
      posthog.identify(order.id);
      posthog.capture('Saved Order', {
        orderPageId: orderPageState.orderPage.id,
        accountId: orderPageState.account.id,
      });
      if (order.deposit_price === 0) {
        navigate(
          `/order/completed?account_id=${orderPageState.account.id}&order_page_id=${
            orderPageState.orderPage.id
          }&transaction_type=${intentToTransactionType(orderPageIntent)}&entity_id=${order.id}`,
        );
        setLoading(false);
        return;
      }
      if (config?.paymentFormType) {
        if (config.hasSessionTimeout) {
          initSessionExpiredTimeout();
        }
        try {
          url = await getPaymentForm(order);
        } catch (e: any) {
          console.error(e);
          setError(
            e?.body?.includes('717')
              ? OrderPageErrors.PHONE_NUMBER_SAVE_ORDER_EXCEPTION
              : OrderPageErrors.SAVE_ORDER_EXCEPTION,
          );
          clearOrderPageStateFromLocalStorage();
          clearSessionExpiry(orderPageState.orderPage.id);
          clearSessionExpiredTimeout();
          setSessionTtlMilliseconds(null);
          setActiveStep(0);
          reloadDates();
          setLoading(false);
          return;
        }
      }
      setOrderPageState({
        ...orderPageState,
        activeStep: currentStepIndex + 1,
        personalDetails,
        paymentUrl: url,
        order_id: order?.id,
        returningCustomer: order?.returning_customer || false,
      });
      setLoading(false);
    }
  };

  const handlePassScheduleNext = async () => {
    if (orderPageState.passScheduleSelections.length === 0 && orderPageState.passDatedSessionSelections.length === 0) {
      return;
    }
    if (!nextIsPayment) {
      moveToNextStep();
      return;
    }
    setLoading(true);
    let url = '';
    const order = await saveOrder(orderPageState.personalDetails);
    if (!order) {
      return;
    }
    posthog.identify(order.id);
    posthog.capture('Saved Order', {
      orderPageId: orderPageState.orderPage.id,
      accountId: orderPageState.account.id,
    });
    if (order.deposit_price === 0) {
      navigate(
        `/order/completed?account_id=${orderPageState.account.id}&order_page_id=${
          orderPageState.orderPage.id
        }&transaction_type=${intentToTransactionType(orderPageIntent)}&entity_id=${order.id}`,
      );
      setLoading(false);
      return;
    }
    if (config?.paymentFormType) {
      if (config.hasSessionTimeout) {
        initSessionExpiredTimeout();
      }
      try {
        url = await getPaymentForm(order);
      } catch (e: any) {
        console.error(e);
        setError(
          e?.body?.includes('717')
            ? OrderPageErrors.PHONE_NUMBER_SAVE_ORDER_EXCEPTION
            : OrderPageErrors.SAVE_ORDER_EXCEPTION,
        );
        clearOrderPageStateFromLocalStorage();
        clearSessionExpiry(orderPageState.orderPage.id);
        clearSessionExpiredTimeout();
        setSessionTtlMilliseconds(null);
        setActiveStep(0);
        reloadDates();
        setLoading(false);
        return;
      }
    }

    setOrderPageState({
      ...orderPageState,
      activeStep: currentStepIndex + 1,
      paymentUrl: url,
      order_id: order?.id,
      returningCustomer: order?.returning_customer || false,
    });
    setLoading(false);
  };

  const handleDisclaimersNext = async () => {
    if (!disclaimersAccepted) {
      setDisclaimersError(true);
      return;
    }

    setDisclaimersError(false);
    setLoading(true);
    let url = '';
    const order = await saveOrder(orderPageState.personalDetails);
    if (!order) {
      return;
    }
    posthog.identify(order.id);
    posthog.capture('Saved Order', {
      orderPageId: orderPageState.orderPage.id,
      accountId: orderPageState.account.id,
    });
    if (order.deposit_price === 0) {
      navigate(
        `/order/completed?account_id=${orderPageState.account.id}&order_page_id=${
          orderPageState.orderPage.id
        }&transaction_type=${intentToTransactionType(orderPageIntent)}&entity_id=${order.id}`,
      );
      setLoading(false);
      return;
    }
    if (config?.paymentFormType) {
      if (config.hasSessionTimeout) {
        initSessionExpiredTimeout();
      }
      try {
        url = await getPaymentForm(order);
      } catch (e: any) {
        console.error(e);
        setError(
          e?.body?.includes('717')
            ? OrderPageErrors.PHONE_NUMBER_SAVE_ORDER_EXCEPTION
            : OrderPageErrors.SAVE_ORDER_EXCEPTION,
        );
        clearOrderPageStateFromLocalStorage();
        clearSessionExpiry(orderPageState.orderPage.id);
        clearSessionExpiredTimeout();
        setSessionTtlMilliseconds(null);
        setActiveStep(0);
        reloadDates();
        setLoading(false);
        return;
      }
    }

    setOrderPageState({
      ...orderPageState,
      activeStep: currentStepIndex + 1,
      paymentUrl: url,
      order_id: order?.id,
      returningCustomer: order?.returning_customer || false,
    });
    setLoading(false);
  };

  const handlePaymentFormNext = async () => {
    setLoading(true);
    await saveOrder();
    navigate(
      `/order/completed?account_id=${orderPageState.account.id}&order_page_id=${orderPageState.orderPage.id}&transaction_type=${orderPageIntent}&entity_id=${orderPageState.order_id}`,
    );
    setLoading(false);
  };

  const handleNext = async (intent?: OrderPageIntent) => {
    window.parent.postMessage({ name: 'monkeybook_scrollToTop' }, '*');
    // the error banner now stays until dismissed, so clear it when a new attempt starts
    setError(undefined);

    switch (currentStepId) {
      case StepId.DETAILS:
      case StepId.VOUCHER_SUMMARY:
      case StepId.DATE_AND_TIME:
      case StepId.PASS_SUMMARY:
        if (personalDetailsRef.current) {
          personalDetailsRef.current.scrollIntoView({ behavior: 'smooth' });
        }
        handleDetailsDateTimeSelectionNext(intent);
        setTimeout(() => {
          window.parent.postMessage({ name: 'monkeybook_scrollToTop' }, '*');
        }, 250);
        break;
      case StepId.PROJECT_SELECTION:
        moveToNextStep();
        break;
      case StepId.GIFT_CARD_AMOUNT:
        if (orderPageState.details.price > 0) {
          moveToNextStep();
        }
        break;
      case StepId.PERSONAL_DETAILS:
      case StepId.GIFT_CARD_PERSONAL_DETAILS:
        await handlePersonalDetailsNext();
        break;
      case StepId.PASS_SCHEDULE_SELECTIONS:
      case StepId.PASS_DATED_SESSION_SELECTIONS:
        await handlePassScheduleNext();
        break;
      case StepId.DISCLAIMERS:
        await handleDisclaimersNext();
        break;
      case StepId.PAYMENT:
        await handlePaymentFormNext();
        break;
      default:
        break;
    }
  };

  const handleBack = () => {
    if (currentStepId === StepId.PAYMENT) {
      if (config?.hasSessionTimeout || hasStepBeforePayment) {
        setIsResetDialogOpen(true);
        return;
      }
    }
    const prevIndex = currentStepIndex - 1;
    const sellsBothTypes = sellsBothVoucherTypes(orderPageState.orderPage as OrderPageData);
    if (currentStepId === StepId.GIFT_CARD_AMOUNT && sellsBothTypes) {
      setActiveStep(prevIndex);
    } else if (currentStepId === StepId.GIFT_CARD_AMOUNT || currentStepId === StepId.GIFT_CARD_TYPE) {
      setOrderPageState({
        ...orderPageState,
        activeStep: prevIndex,
        orderPageIntent: OrderPageIntent.BUY_NOW,
        details: {
          ...orderPageState.details,
          price: quotePrice({
            orderPage: orderPageState.orderPage as OrderPageData,
            details: orderPageState.details,
            dateDetails: orderPageState.dateAndTimeDetails,
            account: orderPageState.account,
          }).total,
        },
      });
    } else if (
      currentStepId === StepId.GIFT_CARD_PERSONAL_DETAILS &&
      (orderPageIntent === OrderPageIntent.MONETARY_GIFT_CARD || sellsBothTypes)
    ) {
      setActiveStep(prevIndex);
    } else if (
      (currentStepId === StepId.PERSONAL_DETAILS || currentStepId === StepId.GIFT_CARD_PERSONAL_DETAILS) &&
      orderPageIntent !== OrderPageIntent.REDEEM_GIFT_CARD &&
      orderPageIntent !== OrderPageIntent.BUY_PASS
    ) {
      setActiveStepAndOrderPageIntent(prevIndex, OrderPageIntent.BUY_NOW);
    } else if (orderPageIntent === OrderPageIntent.REDEEM_GIFT_CARD) {
      setActiveStepAndOrderPageIntent(prevIndex, OrderPageIntent.REDEEM_GIFT_CARD);
    } else if (orderPageIntent === OrderPageIntent.BUY_PASS) {
      setActiveStepAndOrderPageIntent(prevIndex, OrderPageIntent.BUY_PASS);
    } else {
      setActiveStep(prevIndex);
    }
  };

  const handleReset = async () => {
    if (orderPageState.order_id && orderPageIntent === OrderPageIntent.BUY_NOW) {
      await OrderService.abandonOrder(orderPageState.order_id);
    }
    clearOrderPageStateFromLocalStorage();
    clearSessionExpiry(orderPageState.orderPage.id);
    clearSessionExpiredTimeout();
    window.location.reload();
  };

  const nextButtonText = () => {
    if (currentStepId === StepId.PERSONAL_DETAILS && orderPageIntent === OrderPageIntent.REDEEM_GIFT_CARD) {
      return t('redeem');
    }
    if (currentStepId === StepId.DISCLAIMERS) {
      return t('order_page.accept_disclaimers');
    }
    if (currentStepId === StepId.PAYMENT) {
      return t('finish');
    }
    return t('next');
  };

  return {
    handleNext,
    handleBack,
    handleReset,
    nextButtonText,
    isResetDialogOpen,
    setIsResetDialogOpen,
    detailsStepErrors,
  };
}
